import { useTranslation } from '@repo/i18n'
import { Upload } from 'lucide-react'
import { type DropzoneOptions, useDropzone } from 'react-dropzone'
import { objectKeys } from '#lib/object-utils'
import { cn, formatBytes } from '#lib/utils'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '#ui/empty'

interface DropZoneProps {
	className?: string
	dropzoneOptions: DropzoneOptions
}

export function DropZone(props: DropZoneProps) {
	const { t, i18n } = useTranslation()
	const dropzone = useDropzone(props.dropzoneOptions)
	const accept = props.dropzoneOptions.accept ?? {}
	// People read ".pdf", not "application/pdf": show the extensions when the options list them.
	const extensions = Object.values(accept).flat()

	return (
		<Empty
			className={cn(
				'relative border border-input p-0 text-start transition-colors',
				dropzone.isDragActive && 'border-primary bg-primary/5',
				props.className
			)}
		>
			<div
				{...dropzone.getRootProps()}
				className={cn(
					'flex w-full cursor-pointer flex-col items-center justify-center px-4 py-5 transition-colors hover:bg-muted/50 sm:px-6',
					dropzone.isDragActive && 'opacity-0'
				)}
			>
				<EmptyHeader className='gap-1.5'>
					<Upload className='size-4 text-muted-foreground' />
					<EmptyTitle className='font-medium text-sm'>
						{t('shared:drop_zone.drag_and_drop_files')}
					</EmptyTitle>
					<EmptyDescription className='wrap-break-word max-w-xs'>
						{t('shared:drop_zone.accepted_formats', {
							formats: (extensions.length > 0
								? extensions
								: objectKeys(accept)
							).join(', '),
							size: props.dropzoneOptions.maxSize
								? formatBytes(
										props.dropzoneOptions.maxSize,
										i18n.language
									)
								: ''
						})}
					</EmptyDescription>
				</EmptyHeader>
				<input {...dropzone.getInputProps()} className='sr-only' />
			</div>

			{dropzone.isDragActive && (
				<EmptyHeader className='pointer-events-none absolute inset-0 justify-center gap-1.5 px-4 text-primary'>
					<Upload className='size-4' />
					<EmptyTitle className='font-medium text-sm'>
						{t('shared:drop_zone.drop_files_here')}
					</EmptyTitle>
				</EmptyHeader>
			)}
		</Empty>
	)
}
